import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Clock, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";

export default function VerifyEmailPage() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [status, setStatus] = useState("loading");
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const verify = async () => {
      try {
        await api.get(`/auth/verify-email/${token}`);
        setStatus("success");
      } catch (err) {
        if (err.response?.status === 410) {
          setStatus("expired");
        } else {
          setStatus("invalid");
        }
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {status === "loading" && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="verify-loading">
            <Loader2 className="h-12 w-12 text-slate-400 animate-spin mx-auto mb-4" />
            <p className="text-lg text-slate-600">{t("register.verifying")}</p>
          </div>
        )}

        {status === "success" && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="verify-success">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-900">{t("register.verificationSuccess")}</h1>
            <p className="text-sm text-slate-500 mt-2 mb-6">{t("register.verifiedGoLogin")}</p>
            <Link to="/login">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="go-to-login-btn">
                {t("register.goToLogin")}
              </Button>
            </Link>
          </div>
        )}

        {status === "expired" && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="verify-expired">
            <Clock className="h-12 w-12 text-amber-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-900">{t("register.verificationExpired")}</h1>
            <p className="text-sm text-slate-500 mt-2 mb-6">{t("register.verificationExpiredDesc")}</p>
            <Link to="/register">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                {t("register.goToRegister")}
              </Button>
            </Link>
          </div>
        )}

        {status === "invalid" && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="verify-invalid">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-slate-900">{t("register.verificationInvalid")}</h1>
            <p className="text-sm text-slate-500 mt-2 mb-6">{t("register.verificationInvalidDesc")}</p>
            <Link to="/register">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                {t("register.goToRegister")}
              </Button>
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
